'use client'

import { Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { HStack, Input, RadioGroup, Textarea } from '@chakra-ui/react'
import type { Control } from 'react-hook-form'
import type { IQuestion } from '@/entities/questionnaire/types'
import type { SurveyFormValues } from '../model/use-medical-survey-form'

/** Form values are strings/booleans, but a not-yet-registered field is empty. */
const toText = (value: unknown) =>
  value === undefined || value === null ? '' : String(value)

interface Props {
  question: IQuestion
  control: Control<SurveyFormValues>
  disabled?: boolean
  /** `lg` is used by the one-question-at-a-time stepper. */
  size?: 'sm' | 'lg'
  /** Called when picking a yes/no option, so the stepper can move on by itself. */
  onPick?: () => void
}

/**
 * Renders just the answer control for a single question — the caller owns the
 * surrounding layout and the question text.
 */
export function QuestionInput({
  question,
  control,
  disabled,
  size = 'sm',
  onPick,
}: Props) {
  const { t } = useTranslation()
  const large = size === 'lg'

  return (
    <Controller
      control={control}
      name={question.questionId}
      render={({ field }) => {
        if (question.questionType === 'BOOLEAN') {
          return (
            <RadioGroup.Root
              value={toText(field.value)}
              onValueChange={(e) => {
                field.onChange(e.value)
                if (!disabled) onPick?.()
              }}
              disabled={disabled}
              colorPalette="red"
            >
              <HStack gap={6} flexWrap="wrap">
                {[
                  { value: 'true', label: t('common.yes') },
                  { value: 'false', label: t('common.no') },
                ].map((option) => (
                  <RadioGroup.Item
                    key={option.value}
                    value={option.value}
                    cursor={disabled ? 'not-allowed' : 'pointer'}
                  >
                    <RadioGroup.ItemHiddenInput />
                    <RadioGroup.ItemIndicator />
                    <RadioGroup.ItemText fontSize={large ? 'md' : 'sm'}>
                      {option.label}
                    </RadioGroup.ItemText>
                  </RadioGroup.Item>
                ))}
              </HStack>
            </RadioGroup.Root>
          )
        }

        if (question.questionType === 'DATE') {
          return (
            <Input
              type="date"
              size={large ? 'md' : 'sm'}
              value={toText(field.value)}
              onChange={(e) => field.onChange(e.target.value)}
              disabled={disabled}
              bg="bg.surface"
              borderColor="border"
              borderRadius="md"
              maxW="240px"
            />
          )
        }

        return (
          <Textarea
            size={large ? 'md' : 'sm'}
            value={toText(field.value)}
            onChange={(e) => field.onChange(e.target.value)}
            disabled={disabled}
            bg="bg.surface"
            borderColor="border"
            borderRadius="md"
            resize="vertical"
            rows={3}
          />
        )
      }}
    />
  )
}
